import { getInitiatorPedalIds } from './tradeUtils';
import { allowsTrade } from './offerMode';

/**
 * "Resend" for a trade offer the user RECEIVED that expired before they saw
 * it, or that they declined and have since changed their mind about
 * (Offer History). The expired offer belongs to the other person, so it is
 * never revived; instead the user sends a FRESH offer of their own with the
 * mirrored terms: their pedal(s) for the other person's pedal, same cash in
 * the same direction, with the cash left editable on the offer page.
 *
 * Pedal ownership is read from the CURRENT listings, never from the offer's
 * fromUserId/toUserId or its pedal-list positions: counters can flip the
 * offer's direction and swap the initiator pedal list, so the fields alone
 * can't say whose pedal is whose.
 *
 * Pure: the caller fetches the listings (`listingsById`, null for a missing
 * doc) and does all I/O. Returns
 *   { ok: true, targetPedalId, myPedalIds, offerType, cashAmount,
 *     droppedMyPedalNames }
 * or { ok: false, message } with a user-facing reason.
 */

// Same availability bar the Browse / matching surfaces use for a listing
// that can take a new offer.
export function isListingAvailable(listing) {
  if (!listing) return false;
  if (listing.active === false) return false;
  if (listing.inTrade === true) return false;
  if (listing.hiddenFromPedalboard === true) return false;
  if (listing.needsReactivation === true) return false;
  // Resend re-creates a TRADE offer; a listing switched to sale only cannot take one.
  if (!allowsTrade(listing)) return false;
  return true;
}

const pedalName = (listing, fallback) =>
  listing && (listing.make || listing.model)
    ? `${listing.make || ''} ${listing.model || ''}`.trim()
    : fallback;

/**
 * Who declined a declined offer, from its history trail. Every user decline
 * appends { type: 'response', responseType: 'decline', from: <uid> }; the
 * server's auto-decline (a pedal accepted in another trade) appends
 * { type: 'auto_decline', from: 'system' }. The LAST decline entry wins.
 * Returns the decliner's uid, 'system', or null when the trail has no decline
 * entry (legacy docs) — null is treated as unknown, never as "you".
 */
export function declinedBy(offer) {
  const history = Array.isArray(offer?.history) ? offer.history : [];
  for (let i = history.length - 1; i >= 0; i--) {
    const h = history[i] || {};
    if (h.type === 'auto_decline') return 'system';
    if (h.type === 'response' && h.responseType === 'decline') return h.from || null;
  }
  return null;
}

/**
 * Eligible: a TRADE offer in the user's "received" column that either
 * expired, or was declined BY THE USER (they changed their mind). An offer
 * the other person declined, or the system auto-declined, is not theirs to
 * bring back.
 */
export function isResendEligible(offer, myUid) {
  if (!offer || offer.offerType !== 'trade' || !myUid || offer.toUserId !== myUid) return false;
  if (offer.status === 'expired') return true;
  if (offer.status === 'declined') return declinedBy(offer) === myUid;
  return false;
}

export function resendPedalIds(offer) {
  return [...new Set([...getInitiatorPedalIds(offer), offer.responderPedalId].filter(Boolean))];
}

export function buildResendPrefill({ offer, myUid, listingsById }) {
  if (!isResendEligible(offer, myUid)) {
    return { ok: false, message: 'Only expired offers, or offers you declined, can be resent.' };
  }
  const otherUserId = offer.fromUserId;
  if (!otherUserId || otherUserId === myUid) {
    return { ok: false, message: "This offer can't be resent." };
  }

  const pedalIds = resendPedalIds(offer);

  // A deleted listing can't be placed on either side by ownership. It is
  // unavailable either way, so the resend is refused rather than guessing.
  if (pedalIds.some((id) => !listingsById[id])) {
    return { ok: false, message: 'One of the pedals in this offer is no longer listed.' };
  }

  const theirIds = pedalIds.filter((id) => listingsById[id].ownerId === otherUserId);
  const myIds = pedalIds.filter((id) => listingsById[id].ownerId === myUid);

  // A pedal that now belongs to neither of you was traded or sold elsewhere.
  if (theirIds.length + myIds.length !== pedalIds.length) {
    return { ok: false, message: 'One of the pedals in this offer has changed hands since it expired.' };
  }

  if (theirIds.length === 0) {
    return { ok: false, message: 'Their pedal from this offer is no longer listed.' };
  }
  // The offer page targets exactly one of the other person's pedals, so a
  // multi-pedal offer from them can't be mirrored as the same terms.
  if (theirIds.length > 1) {
    return {
      ok: false,
      message: "This offer included more than one of their pedals, and a new offer can only ask for one. Open one of their listings to make a new offer.",
    };
  }

  const targetPedalId = theirIds[0];
  if (!isListingAvailable(listingsById[targetPedalId])) {
    return {
      ok: false,
      message: `Their ${pedalName(listingsById[targetPedalId], 'pedal')} is no longer available.`,
    };
  }

  if (myIds.length === 0) {
    return { ok: false, message: 'Your pedal from this offer is no longer listed.' };
  }
  const availableMine = myIds.filter((id) => isListingAvailable(listingsById[id]));
  if (availableMine.length === 0) {
    return {
      ok: false,
      message: myIds.length === 1
        ? `Your ${pedalName(listingsById[myIds[0]], 'pedal')} is no longer available.`
        : 'None of your pedals from this offer are available anymore.',
    };
  }
  const droppedMyPedalNames = myIds
    .filter((id) => !availableMine.includes(id))
    .map((id) => pedalName(listingsById[id], 'pedal'));

  // Cash, mirrored. cashFlow is written on every cash offer and rewritten on
  // every counter, so it is the authoritative "who pays". Legacy docs
  // without it fall back to `type`, which is relative to the sender.
  const amount = Number(offer.counterAmount ?? offer.cashAmount ?? 0) || 0;
  let offerType = 'straight';
  let cashAmount = '';
  if (amount > 0) {
    let payer = offer.cashFlow?.payer || null;
    if (!payer) {
      if (offer.type === 'offer') payer = offer.fromUserId;
      else if (offer.type === 'ask') payer = offer.toUserId;
    }
    if (payer === myUid) offerType = 'offer';          // I pay them, as before
    else if (payer === otherUserId) offerType = 'ask'; // they pay me, as before
    else offerType = '';                               // unknown: let the user choose
    cashAmount = offerType ? String(amount) : '';
  }

  return {
    ok: true,
    targetPedalId,
    myPedalIds: availableMine,
    offerType,
    cashAmount,
    droppedMyPedalNames,
  };
}
